import { type EdgeLabel, type GraphLabel, graphlib, layout, type NodeLabel } from '@dagrejs/dagre'
import { BUILT_IN_TASKS } from '@zerohuman/shared'
import type { TaskRow } from '@/api'
import { runPillStatus } from '@/lib/statusTone'

export const TASK_GRAPH_NODE_WIDTH = 248
export const TASK_GRAPH_NODE_HEIGHT = 136
export const TASK_GRAPH_COLUMN_GAP = 180
export const TASK_GRAPH_ROW_GAP = 80

/**
 * One node on a task graph. A node is either a task in the graph's own set, or the
 * off-canvas predecessor or successor outside it (`taskId: null`).
 */
export type TaskGraphNode = {
    /** Node id on the canvas: the task slug, because successors point at slugs. */
    slug: string
    name: string
    kind: string | null
    /** The task this node opens; null when the node sits outside the graph's task set. */
    taskId: string | null
    goal: string | null
    /** Who the task's runs go to, as the card reads it (see `runsAsLabel`); null for an unknown neighbor. */
    runsAs: string | null
    /** The owning role, when known, lets a role map navigate to an external task's workflow. */
    roleId: string | null
    roleName: string | null
    /** Draw outside-view styling for external neighbors and COO tasks shown in a team's workflow. */
    offCanvas: boolean
    /**
     * How this task last ran (the run outcome, e.g. succeeded/failed), so a reader sees the
     * loop's health on the canvas. Null when the task has never run, and on an off-canvas
     * placeholder, which is not a task of this graph and so has no run of its own here.
     */
    lastRunStatus: string | null
    /** Top-left canvas coordinates, including space for connections and disconnected workflows. */
    position: { x: number; y: number }
    /** Legacy grid coordinates. The canvas should use `position` for precise placement. */
    column: number
    row: number
}

/** One successor edge. `dangling` means either endpoint is outside the graph's task set. */
export type TaskGraphEdge = {
    id: string
    source: string
    target: string
    /** Run outcome the successor fires on, including named outcomes such as `changes_requested`. */
    on: string | null
    when: string | null
    dangling: boolean
    /** Separate parallel outcomes instead of drawing indistinguishable paths. */
    parallelIndex: number
    parallelCount: number
    /** Includes self loops, which need routing around the card rather than through it. */
    isBackEdge: boolean
    /** Backward edges whose horizontal spans overlap receive distinct return lanes. */
    returnLane: number
    /** Routed canvas coordinates from source to target, including component packing offsets. */
    points: Array<{ x: number; y: number }>
}

export type TaskGraph = {
    nodes: TaskGraphNode[]
    edges: TaskGraphEdge[]
}

export type TaskGraphOptions = {
    scope?: 'team' | 'role'
    /** Direct neighbor metadata; only connections to the graph's own tasks are included. */
    relatedTasks?: TaskRow[]
}

/** Shown when a tenant task has no role, so nobody can run it. */
export const UNASSIGNED_LABEL = 'Unassigned'

/** Who a task's runs go to: whoever holds its role on its team, or the OS for an OS task. Never a member. */
export function runsAsLabel(task: TaskRow): string {
    if (task.origin === 'os') return 'OS'
    return task.assigneeRoleName ?? task.assigneeRole ?? UNASSIGNED_LABEL
}

/**
 * Dagre temporarily reverses cycle edges while ranking and reduces edge crossings.
 * Layout each connected workflow independently so an unrelated task cannot stretch its
 * connections, then pack those workflows into rows (including a compact grid of standalones).
 */
function placeNodes(slugs: string[], edges: TaskGraphEdge[]): Map<string, { x: number; y: number }> {
    const topology = new graphlib.Graph()
    for (const slug of [...slugs].sort()) topology.setNode(slug)
    for (const edge of edges) topology.setEdge(edge.source, edge.target)
    const components = graphlib.alg.components(topology).map(component => component.sort())
    components.sort((a, b) => b.length - a.length || (a[0] ?? '').localeCompare(b[0] ?? ''))

    const layouts = components.map(component => {
        const graph = new graphlib.Graph<GraphLabel, NodeLabel, EdgeLabel>()
        graph.setGraph({
            rankdir: 'LR',
            ranksep: TASK_GRAPH_COLUMN_GAP,
            nodesep: TASK_GRAPH_ROW_GAP,
            edgesep: 36,
            acyclicer: 'greedy',
        })
        for (const slug of component) {
            graph.setNode(slug, { width: TASK_GRAPH_NODE_WIDTH, height: TASK_GRAPH_NODE_HEIGHT })
        }
        for (const edge of edges) {
            if (graph.hasNode(edge.source)) {
                // Rank each relationship once. Repeated outcomes are rendered separately below;
                // passing them to Dagre as parallel edges can create degenerate intersection paths.
                graph.setEdge(edge.source, edge.target, { weight: edge.on === 'terminal' ? 1 : 3 })
            }
        }
        layout(graph)
        return {
            graph,
            width: graph.graph().width ?? TASK_GRAPH_NODE_WIDTH,
            height: graph.graph().height ?? TASK_GRAPH_NODE_HEIGHT,
        }
    })

    const rowWidth = Math.max(
        TASK_GRAPH_NODE_WIDTH * 3 + TASK_GRAPH_COLUMN_GAP * 2,
        ...layouts.map(component => component.width),
    )
    const positions = new Map<string, { x: number; y: number }>()
    let x = 0
    let y = 0
    let rowHeight = 0
    for (const component of layouts) {
        if (x > 0 && x + component.width > rowWidth) {
            x = 0
            y += rowHeight + TASK_GRAPH_ROW_GAP * 2
            rowHeight = 0
        }
        for (const slug of component.graph.nodes()) {
            const node = component.graph.node(slug)
            positions.set(slug, {
                x: x + (node.x ?? 0) - TASK_GRAPH_NODE_WIDTH / 2,
                y: y + (node.y ?? 0) - TASK_GRAPH_NODE_HEIGHT / 2,
            })
        }
        for (const edge of edges) {
            if (component.graph.hasNode(edge.source)) {
                edge.points = (component.graph.edge(edge.source, edge.target)?.points ?? []).map(point => ({
                    x: x + point.x,
                    y: y + point.y,
                }))
            }
        }
        x += component.width + TASK_GRAPH_COLUMN_GAP
        rowHeight = Math.max(rowHeight, component.height)
    }
    return positions
}

/** A node's card as a routing obstacle, the shape every workflow canvas routes edges around. */
export function nodeObstacles(nodes: Array<{ position: { x: number; y: number } }>) {
    return nodes.map(node => ({ ...node.position, width: TASK_GRAPH_NODE_WIDTH, height: TASK_GRAPH_NODE_HEIGHT }))
}

/** Share stable task-card placement and loop/parallel routing across workflow and execution canvases. */
export function layoutTaskGraph(slugs: string[], edges: TaskGraphEdge[]): Map<string, { x: number; y: number }> {
    // Sorting before layout makes positions stable when API pagination/order changes.
    edges.sort((a, b) => a.id.localeCompare(b.id))
    const positions = placeNodes(slugs, edges)
    const parallelEdges = new Map<string, TaskGraphEdge[]>()
    for (const edge of edges) {
        const key = JSON.stringify([edge.source, edge.target])
        const group = parallelEdges.get(key) ?? []
        group.push(edge)
        parallelEdges.set(key, group)
        edge.isBackEdge = (positions.get(edge.target)?.x ?? 0) <= (positions.get(edge.source)?.x ?? 0)
    }
    for (const group of parallelEdges.values()) {
        group.forEach((edge, index) => {
            edge.parallelIndex = index
            edge.parallelCount = group.length
        })
    }
    const returnLanes: Array<Array<{ left: number; right: number }>> = []
    for (const edge of edges.filter(candidate => candidate.isBackEdge)) {
        const span = {
            left: positions.get(edge.target)?.x ?? 0,
            right: (positions.get(edge.source)?.x ?? 0) + TASK_GRAPH_NODE_WIDTH,
        }
        const available = returnLanes.findIndex(candidate =>
            candidate.every(other => other.right < span.left || other.left > span.right),
        )
        edge.returnLane = available < 0 ? returnLanes.length : available
        const lane = returnLanes[edge.returnLane] ?? []
        lane.push(span)
        returnLanes[edge.returnLane] = lane
    }

    return positions
}

/**
 * Draw each task's successors and direct predecessors supplied in relatedTasks.
 * External neighbors keep their connections to in-scope tasks, flagged `dangling`,
 * without recursively expanding the workflow. Self-improvement and its connections
 * are omitted because that loop runs independently.
 */
export function buildTaskGraph(tasks: TaskRow[], options: TaskGraphOptions = {}): TaskGraph {
    const relatedTaskBySlug = new Map((options.relatedTasks ?? []).map(task => [task.slug, task]))
    const hidden = new Set(
        [...BUILT_IN_TASKS, ...tasks, ...(options.relatedTasks ?? [])]
            .filter(task => task.builtInRole === 'analysis')
            .map(task => task.slug),
    )
    const included = (slug: string) => !hidden.has(slug)
    const visibleTasks = tasks.filter(task => included(task.slug))
    const slugs = new Set(visibleTasks.map(task => task.slug))
    const relatedTasks = [...relatedTaskBySlug.values()].filter(task => included(task.slug) && !slugs.has(task.slug))

    function ownership(task?: TaskRow) {
        return {
            runsAs: task ? runsAsLabel(task) : null,
            roleId: task?.assigneeRoleId ?? null,
            roleName: task?.assigneeRoleName ?? null,
        }
    }

    const edges: TaskGraphEdge[] = []
    for (const task of [...visibleTasks, ...relatedTasks]) {
        for (const [index, successor] of (task.currentVersion?.successors ?? []).entries()) {
            if (!included(successor.slug)) continue
            // A neighbor's other connections belong to its own workflow. Include only
            // incoming connections whose target is one of this view's original tasks.
            if (!slugs.has(task.slug) && !slugs.has(successor.slug)) continue
            edges.push({
                // Two successors of one task can share a target on different outcomes
                // (`succeeded` and `failed` both going to cleanup). The entry index keeps
                // their ids distinct, so the canvas draws both instead of keying one out.
                id: `${task.slug}->${successor.slug}#${index}`,
                source: task.slug,
                target: successor.slug,
                on: successor.on ?? null,
                when: successor.when ?? null,
                dangling: !slugs.has(task.slug) || !slugs.has(successor.slug),
                parallelIndex: 0,
                parallelCount: 1,
                isBackEdge: false,
                returnLane: 0,
                points: [],
            })
        }
    }

    const offCanvasSlugs: string[] = []
    for (const edge of edges) {
        for (const slug of [edge.source, edge.target]) {
            if (!slugs.has(slug) && !offCanvasSlugs.includes(slug)) {
                offCanvasSlugs.push(slug)
            }
        }
    }

    const positions = layoutTaskGraph([...slugs, ...offCanvasSlugs], edges)

    function place(slug: string) {
        const position = positions.get(slug) ?? { x: 0, y: 0 }
        return {
            position,
            column: position.x / (TASK_GRAPH_NODE_WIDTH + TASK_GRAPH_COLUMN_GAP),
            row: position.y / (TASK_GRAPH_NODE_HEIGHT + TASK_GRAPH_ROW_GAP),
        }
    }

    const nodes: TaskGraphNode[] = []
    for (const task of visibleTasks) {
        nodes.push({
            slug: task.slug,
            name: task.name ?? task.slug,
            kind: task.kind,
            taskId: task.id,
            goal: task.currentVersion?.goal ?? null,
            ...ownership(task),
            offCanvas: options.scope === 'team' && task.assigneeRole === 'coo',
            // A last run that dead-ended reads stopped, as it does on the run's own page (`runPillStatus`).
            lastRunStatus: task.lastRunStatus
                ? runPillStatus({ status: task.lastRunStatus, stoppedReason: task.lastRunStoppedReason })
                : null,
            ...place(task.slug),
        })
    }
    for (const slug of offCanvasSlugs) {
        const task = relatedTaskBySlug.get(slug)
        nodes.push({
            slug,
            name: task?.name ?? slug,
            kind: task?.kind ?? null,
            taskId: null,
            goal: task?.currentVersion?.goal ?? null,
            ...ownership(task),
            offCanvas: true,
            lastRunStatus: null,
            ...place(slug),
        })
    }
    return { nodes, edges }
}
